import { useState } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import { ScrollArea } from "./ui/scroll-area";
import { Badge } from "./ui/badge";
import GlobalSearch from "./GlobalSearch";
import {
  LayoutDashboard,
  MessageSquare,
  Users,
  LogOut,
  Menu,
  X,
  Camera,
  ChevronRight,
  Code,
  Shield,
  UserCog,
  Calendar,
  Settings,
  Search,
} from "lucide-react";

export default function DashboardLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const { user, logout, isAdmin } = useAuth();
  const navigate = useNavigate();

  // Menú dinámico según rol
  const navItems = [
    { path: "/", icon: LayoutDashboard, label: "Dashboard" },
    { path: "/consultas", icon: MessageSquare, label: "Consultas" },
    { path: "/clientes", icon: Users, label: "Clientes" },
    { path: "/calendario", icon: Calendar, label: "Calendario" },
    { path: "/configuracion", icon: Settings, label: "Configuración" },
    ...(isAdmin ? [{ path: "/usuarios", icon: UserCog, label: "Usuarios" }] : []),
  ];

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const NavItem = ({ item }) => (
    <NavLink
      to={item.path}
      end={item.path === "/"}
      onClick={() => setSidebarOpen(false)}
      className={({ isActive }) =>
        `flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 group ${isActive
          ? "bg-white text-primary shadow-soft"
          : "text-primary/70 hover:bg-white/50 hover:text-primary"
        }`
      }
    >
      {({ isActive }) => (
        <>
          <item.icon className="w-5 h-5" strokeWidth={1.5} />
          <span className="font-medium">{item.label}</span>
          {isActive && (
            <ChevronRight className="w-4 h-4 ml-auto" strokeWidth={1.5} />
          )}
        </>
      )}
    </NavLink>
  );

  return (
    <div className="min-h-screen bg-background">
      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-xl border-b border-border/40 px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Camera className="w-6 h-6 text-primary" strokeWidth={1.5} />
            <span className="font-heading font-bold text-primary">FCB</span>
          </div>
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="icon"
              data-testid="mobile-search-btn"
              onClick={() => setSearchOpen(true)}
            >
              <Search className="w-5 h-5" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              data-testid="mobile-menu-btn"
              onClick={() => setSidebarOpen(!sidebarOpen)}
            >
              {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </Button>
          </div>
        </div>
      </header>

      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-40 w-72 bg-muted transform transition-transform duration-300 ease-in-out lg:translate-x-0 ${sidebarOpen ? "translate-x-0" : "-translate-x-full"
          }`}
      >
        <div className="flex flex-col h-full p-6">
          {/* Logo */}
          <div className="flex items-center gap-3 mb-8 pt-2">
            <div className="w-10 h-10 bg-primary rounded-xl flex items-center justify-center">
              <Camera className="w-6 h-6 text-white" strokeWidth={1.5} />
            </div>
            <div>
              <h1 className="font-heading font-bold text-primary text-lg leading-tight">
                Fotomatón
              </h1>
              <p className="text-xs text-muted-foreground">Costa Brava</p>
            </div>
          </div>

          {/* Search Button */}
          <button
            onClick={() => setSearchOpen(true)}
            className="flex items-center gap-3 w-full px-4 py-2.5 mb-4 rounded-xl text-sm text-muted-foreground bg-white/60 hover:bg-white border border-border/40 transition-all duration-200 group"
          >
            <Search className="w-4 h-4" strokeWidth={1.5} />
            <span className="flex-1 text-left">Buscar...</span>
            <kbd className="hidden lg:inline-flex h-5 select-none items-center gap-0.5 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium text-muted-foreground">
              ⌘K
            </kbd>
          </button>

          {/* Navigation */}
          <ScrollArea className="flex-1 -mx-2 px-2">
            <nav className="space-y-2">
              {navItems.map((item) => (
                <NavItem key={item.path} item={item} />
              ))}
            </nav>
          </ScrollArea>

          {/* User Section */}
          <div className="mt-6 pt-6 border-t border-border/40">
            <div className="flex items-center gap-3 mb-4">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${isAdmin ? "bg-secondary/10" : "bg-primary/10"
                }`}>
                {isAdmin ? (
                  <Shield className="w-5 h-5 text-secondary" />
                ) : (
                  <span className="text-primary font-semibold">
                    {user?.name?.charAt(0)?.toUpperCase() || "U"}
                  </span>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-medium text-primary truncate">
                    {user?.name || "Usuario"}
                  </p>
                  {isAdmin && (
                    <Badge variant="outline" className="text-[10px] px-1.5 py-0 bg-secondary/10 text-secondary border-secondary/30">
                      Admin
                    </Badge>
                  )}
                </div>
                <p className="text-xs text-muted-foreground truncate">
                  {user?.email || ""}
                </p>
              </div>
            </div>
            <Button
              variant="ghost"
              data-testid="logout-btn"
              onClick={handleLogout}
              className="w-full justify-start gap-3 text-muted-foreground hover:text-destructive hover:bg-destructive/10"
            >
              <LogOut className="w-4 h-4" strokeWidth={1.5} />
              Cerrar sesión
            </Button>
          </div>
        </div>
      </aside>

      {/* Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/20 backdrop-blur-sm z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      <main className="lg:ml-72 min-h-screen pt-16 lg:pt-0">
        <div className="p-6 lg:p-8">
          <Outlet />
        </div>
      </main>

      {/* Global Search Dialog */}
      <GlobalSearch open={searchOpen} onOpenChange={setSearchOpen} />
    </div>
  );
}
